"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, LockKeyhole, ShieldCheck } from "lucide-react";
import { ErrorNotice, Field } from "@/components/ui";
import { errorMessage, mutation } from "@/lib/api";

export function ForgotPasswordPage() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  return (
    <main className="sign-in">
      <form
        className="sign-in-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setPending(true);
          setError("");
          try {
            const data = new FormData(event.currentTarget);
            await mutation("/auth/password-reset/request", {
              email: String(data.get("email") || ""),
            });
            setSent(true);
          } catch (cause) {
            setError(errorMessage(cause));
          } finally {
            setPending(false);
          }
        }}
      >
        <div className="sign-in-heading">
          <LockKeyhole size={22} />
          <h1>Reset your password</h1>
        </div>
        <p className="sign-in-help">
          Enter the email address on your account. If it matches, we’ll send a
          one-time reset link.
        </p>
        <ErrorNotice message={error} />
        {sent ? (
          <p className="configuration-saved" role="status">
            If an account matches that email, reset instructions will arrive
            shortly. Check your inbox and spam folder.
          </p>
        ) : (
          <>
            <Field label="Account email">
              <input
                name="email"
                type="email"
                autoComplete="email"
                maxLength={254}
                required
                autoFocus
              />
            </Field>
            <button className="button button-primary" disabled={pending}>
              {pending ? "Please wait..." : "Send reset link"}
              <ArrowRight size={17} />
            </button>
          </>
        )}
        <Link className="sign-in-back" href="/">
          <ArrowLeft size={14} />
          Back to sign in
        </Link>
      </form>
    </main>
  );
}

export function ResetPasswordPage() {
  const [token, setToken] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const value = new URLSearchParams(window.location.search).get("token") || "";
      setToken(value);
      if (value) window.history.replaceState(null, "", window.location.pathname);
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <main className="sign-in">
      <form
        className="sign-in-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setPending(true);
          setError("");
          const data = new FormData(event.currentTarget);
          const password = String(data.get("password") || "");
          if (password !== String(data.get("confirmPassword") || "")) {
            setError("The passwords do not match.");
            setPending(false);
            return;
          }
          try {
            await mutation("/auth/password-reset/complete", {
              token,
              newPassword: password,
            });
            setComplete(true);
            window.history.replaceState(null, "", "/reset-password");
          } catch (cause) {
            setError(errorMessage(cause));
          } finally {
            setPending(false);
          }
        }}
      >
        <div className="sign-in-heading">
          <ShieldCheck size={22} />
          <h1>Choose a new password</h1>
        </div>
        {complete ? (
          <>
            <p className="configuration-saved" role="status">
              Your password has been reset. You can now sign in.
            </p>
            <Link className="button button-primary" href="/">
              Back to sign in
              <ArrowRight size={17} />
            </Link>
          </>
        ) : (
          <>
            <p className="sign-in-help">
              Use a unique password with at least 12 characters. Reset links
              expire after 30 minutes and can only be used once.
            </p>
            <ErrorNotice message={error} />
            {!token ? (
              <ErrorNotice message="This reset link is missing or invalid. Request a new one to continue." />
            ) : (
              <>
                <Field label="New password">
                  <input
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    minLength={12}
                    maxLength={1024}
                    required
                    autoFocus
                  />
                </Field>
                <Field label="Confirm new password">
                  <input
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    minLength={12}
                    maxLength={1024}
                    required
                  />
                </Field>
                <button className="button button-primary" disabled={pending}>
                  {pending ? "Please wait..." : "Reset password"}
                  <ArrowRight size={17} />
                </button>
              </>
            )}
            <Link className="sign-in-back" href="/forgot-password">
              <ArrowLeft size={14} />
              Request another reset link
            </Link>
          </>
        )}
      </form>
    </main>
  );
}
